// ─── Push Notification Registration Hook ──────────────────────────────
// Extracted from NotificationsContext so push registration still works
// without the context wrapper.  Notification TAP routing lives in
// app/_layout.tsx (notificationToRoute — canonical, auth-aware).

import { useEffect, useState, useCallback } from 'react';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { useAuth } from '@/context/AuthContext';
import { registerPushTokenAPI } from '@/lib/api/notifications';
import { logEvent } from '@/lib/analytics';

// once-per-session guard: registration re-runs on auth changes
let _pushRegisteredLogged = false;

// Configure how notifications appear when the app is in the foreground.
// This only needs to run once — we do it here to ensure it's set up
// even without the NotificationsContext provider.
if (typeof Notifications.setNotificationHandler === 'function') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldPlaySound: true,
      shouldSetBadge: false,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });
}

export function usePushNotifications() {
  const { session, isAuthenticated } = useAuth();
  const token = session?.accessToken;
  const [pushToken, setPushToken] = useState<string | null>(null);


  const registerForPushNotifications = useCallback(async () => {
    if (Platform.OS === 'web') return;
    if (!isAuthenticated || !token) return;

    try {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;

      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      if (finalStatus !== 'granted') {
        console.warn('Push notification permission not granted');
        return;
      }

      if (Platform.OS === 'android') {
        await Notifications.setNotificationChannelAsync('default', {
          name: 'default',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#FFFE77',
        });
      }

      const { data: devicePushToken } = await Notifications.getDevicePushTokenAsync();
      const appVersion = Constants.expoConfig?.version ?? '1.0.0';
      await registerPushTokenAPI(token, devicePushToken, Platform.OS, appVersion);
      setPushToken(devicePushToken);
      if (!_pushRegisteredLogged) {
        _pushRegisteredLogged = true;
        logEvent('push_registered', { platform: Platform.OS });
      }
    } catch (error) {
      console.error('Error registering for push notifications:', error);
    }
  }, [isAuthenticated, token]);

  // Register for push when auth is ready
  useEffect(() => {
    if (isAuthenticated && token) {
      registerForPushNotifications();
    } else {
      setPushToken(null);
    }
  }, [isAuthenticated, token, registerForPushNotifications]);

  return { pushToken };
}